import React from 'react';
import { ChevronLeft, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

interface SystemReportPageSkeletonProps {
  /**
   * Additional class names
   */
  className?: string;
}

/**
 * SystemReportPageSkeleton component
 *
 * This component provides a loading skeleton specifically designed for the System Report Generate page.
 * It mimics the structure of the initial view of the system report generation process.
 *
 * @example
 * <SystemReportPageSkeleton />
 */
export const SystemReportPageSkeleton: React.FC = () => {
  return (
    <main className="min-h-screen bg-white pt-4 pb-24">
      <div className="max-w-[1200px] mx-auto p-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-[#7A7D81] mb-4">
            <div className="h-4 w-32 bg-gray-200 rounded-md animate-pulse"></div>
          </div>
          <div className="h-8 w-80 bg-gray-200 rounded-md animate-pulse"></div>
        </div>

        {/* Main Content */}
        <div className="bg-white">
          {/* Scope Selection */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-8 w-64 bg-gray-200 rounded-md animate-pulse"></div>
            </div>

            {/* Date Selection Section */}
            <div className="mb-8">
              <div className="h-6 w-48 bg-gray-200 rounded-md animate-pulse mb-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <div className="h-5 w-24 bg-gray-200 rounded-md animate-pulse"></div>
                  <div className="h-10 w-full bg-gray-200 rounded-xl animate-pulse"></div>
                </div>
                <div className="flex flex-col gap-2">
                  <div className="h-5 w-24 bg-gray-200 rounded-md animate-pulse"></div>
                  <div className="h-10 w-full bg-gray-200 rounded-xl animate-pulse"></div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              {/* Tabs Container */}
              <div className="flex flex-col md:flex-row gap-4">
                {/* Individual Report Tab */}
                <div className="flex-1 relative rounded-xl border border-gray-200 transition-colors bg-white">
                  <div className="relative p-4 group">
                    <div className="flex items-center gap-3">
                      <div className="h-5 w-5 bg-gray-200 rounded-md animate-pulse"></div>
                      <div>
                        <div className="h-5 w-40 bg-gray-200 rounded-md animate-pulse"></div>
                        <div className="h-4 w-56 bg-gray-200 rounded-md animate-pulse mt-1"></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Batch Report Tab */}
                <div className="flex-1 relative rounded-xl border border-gray-200 transition-colors bg-white">
                  <div className="relative p-4 group">
                    <div className="flex items-center gap-3">
                      <div className="h-5 w-5 bg-gray-200 rounded-md animate-pulse"></div>
                      <div>
                        <div className="h-5 w-40 bg-gray-200 rounded-md animate-pulse"></div>
                        <div className="h-4 w-56 bg-gray-200 rounded-md animate-pulse mt-1"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Enter Report Details Form */}
              <div
                className="bg-white rounded-xl border border-[#E4E8EA] p-6 overflow-hidden flex flex-col"
                style={{ minHeight: '400px' }}
              >
                <div className="h-6 w-48 bg-gray-200 rounded-md animate-pulse mb-6"></div>

                {/* Student Search Input */}
                <div className="flex flex-col gap-6 overflow-y-auto flex-1 pr-2">
                  <div className="relative">
                    <div className="h-10 w-full bg-gray-200 rounded-xl animate-pulse"></div>
                  </div>

                  {/* Skeleton for student list - fixed height with scroll */}
                  <div className="h-[240px] overflow-y-auto">
                    {[...Array(6)].map((_, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F8F9] rounded-lg mb-2 animate-pulse"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <div className="h-5 w-32 bg-gray-200 rounded-md"></div>
                        <div className="h-5 w-5 bg-gray-200 rounded-md ml-auto"></div>
                      </div>
                    ))}
                  </div>

                  {/* Detailed Report Checkbox */}
                  <div className="flex items-center gap-3">
                    <div className="h-5 w-5 bg-gray-200 rounded-sm animate-pulse"></div>
                    <div className="h-5 w-48 bg-gray-200 rounded-md animate-pulse"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E4E8EA] shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
        <div className="max-w-[1200px] mx-auto px-4 py-4">
          <div className="h-[72px] flex items-center justify-end">
            <div className="h-10 w-48 bg-gray-200 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </div>
    </main>
  );
};
